import React, { useRef, useEffect, useState } from 'react';
import MapFrame from '../../components/MapFrame';
import RSVPForm         from '../../components/RSVPForm';
import Wishlist         from '../../components/Wishlist';
import Countdown        from '../../components/Countdown';
import Schedule         from '../../components/Schedule';
import DresscodePalette from '../../components/DresscodePalette';
import CalendarButton   from '../../components/CalendarButton';
import FixedCTA         from '../../components/FixedCTA';
import YandexMap        from '../../components/YandexMap';
import useReveal        from '../../components/useReveal';
import BlockSubtitle    from '../../components/BlockSubtitle';
import PaidWatermark from '../../components/PaidWatermark';
import MusicPlayer from '../../components/MusicPlayer';
import './Voyage.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;
const src = u => !u ? null : (u.startsWith('http') ? u : u);

/* Convert "#RRGGBB" / "rgb(...)" to "r, g, b" so CSS can plug it into
   rgba(var(--vy-accent-rgb), α) for translucent backgrounds. */
function accentToRgb(c) {
  if (!c) return '101, 121, 255';
  const s = String(c).trim();
  if (s.startsWith('#')) {
    const h = s.slice(1);
    const full = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
    if (full.length >= 6) {
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      if (!Number.isNaN(r + g + b)) return `${r}, ${g}, ${b}`;
    }
  }
  const m = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (m) return `${m[1]}, ${m[2]}, ${m[3]}`;
  return '101, 121, 255';
}

const fmtDateDots = raw => {
  if (!raw) return '??.??.????';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join('.');
};

/* Load template fonts once. */
function useVoyageFonts() {
  useEffect(() => {
    const id = 'vy-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400&family=Libre+Barcode+39+Extended&display=swap';
    document.head.appendChild(link);
  }, []);
}

/* Cubic ease-in-out — natural acceleration at start, deceleration at end. */
const VOYAGE_BUILD_TAG = 'voyage-r2-2026-05-08';
if (typeof window !== 'undefined') window.__voyage_build = VOYAGE_BUILD_TAG;
const easeInOutCubic = (t) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/* Smooth fade ramp using a cosine curve so opacity approaches its target
   without a hard linear edge. */
const easeInOutSine = (t) => -(Math.cos(Math.PI * t) - 1) / 2;

/* ── Flying plane component — drives the plane position AND the comet-tail
   trail from a single JS animation loop. The trail "fades from the plane
   backwards" via an SVG <linearGradient> whose stops are nudged each
   frame to follow the plane's position. */
function FlyingPlane({
  pathRef, svgRef, maskRef, lineRef,
  gradTailRef, gradHeadRef,
  planeSrc, durationSec = 9, phaseOffset = 0,
}) {
  const planeRef = useRef(null);
  useEffect(() => {
    const path = pathRef.current, svg = svgRef.current, plane = planeRef.current;
    const mask = maskRef?.current, line = lineRef?.current;
    const tailStop = gradTailRef?.current, headStop = gradHeadRef?.current;
    if (!path || !svg || !plane) return;
    let raf;
    const start = performance.now();
    /* Cycle phases (0..1 fractions of `durationSec`):
       0.00–0.05  rest before take-off (plane & trail invisible)
       0.05–0.12  plane fades in at start
       0.12–0.72  cruise — ease-in-out flight + trail draws in sync
       0.72–0.82  plane gracefully fades out at the end
       0.82–0.95  trail itself fades after the plane has gone
       0.95–1.00  full rest before next departure */
    const TAIL_LENGTH = 0.30; // fraction of full path that stays visible behind plane
    const tick = (now) => {
      const elapsed = (now - start) / 1000;
      const cycle = ((elapsed / durationSec) + phaseOffset) % 1;
      let progress, planeOpacity, trailOpacity;
      if (cycle < 0.05) {
        progress = 0; planeOpacity = 0; trailOpacity = 0;
      } else if (cycle < 0.12) {
        progress = 0;
        planeOpacity = easeInOutSine((cycle - 0.05) / 0.07);
        trailOpacity = 0;
      } else if (cycle < 0.72) {
        progress = easeInOutCubic((cycle - 0.12) / 0.60);
        planeOpacity = 1;
        trailOpacity = 1;
      } else if (cycle < 0.82) {
        progress = 1;
        planeOpacity = 1 - easeInOutSine((cycle - 0.72) / 0.10);
        trailOpacity = 1;
      } else if (cycle < 0.95) {
        progress = 1;
        planeOpacity = 0;
        trailOpacity = 1 - easeInOutSine((cycle - 0.82) / 0.13);
      } else {
        progress = 0; planeOpacity = 0; trailOpacity = 0;
      }
      /* Plane position — sampled from the path at `progress` distance,
         then mapped into the SVG's actual on-screen rect. */
      const total = path.getTotalLength();
      const pt = path.getPointAtLength(progress * total);
      const svgRect = svg.getBoundingClientRect();
      const vb = svg.viewBox.baseVal;
      const x = (pt.x / vb.width)  * svgRect.width;
      const y = (pt.y / vb.height) * svgRect.height;
      const w = plane.offsetWidth, h = plane.offsetHeight;
      plane.style.transform = `translate(${x - w / 2}px, ${y - h / 2}px)`;
      plane.style.opacity = planeOpacity * 0.9;
      if (mask) mask.style.strokeDashoffset = String(100 - progress * 100);
      if (line) line.style.opacity = String(trailOpacity * 0.7);
      /* Comet tail — gradient stops follow the plane: tail-stop is
         transparent (oldest part of trail) and head-stop sits at the
         plane's current position fully opaque, so the trail visibly
         fades from the plane backwards. */
      if (tailStop && headStop) {
        const tailStart = Math.max(0, progress - TAIL_LENGTH);
        tailStop.setAttribute('offset', String(tailStart));
        headStop.setAttribute('offset', String(progress));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [pathRef, svgRef, maskRef, lineRef, gradTailRef, gradHeadRef, durationSec, phaseOffset]);
  return (
    <img ref={planeRef} className="vy-cd-plane" src={planeSrc} alt=""
         aria-hidden="true" />
  );
}

/* ── Quote panel with the flying plane decoration ──────────────── */
function FlightDeco({ footerQuote }) {
  const svgRef = useRef(null);
  const pathRef = useRef(null);
  /* Two independent planes flying along the same curve — second plane
     takes off the moment the first reaches the end (phase offset 0.28
     of the 9 s cycle, mirroring the cruise-end keyTime 0.72). */
  const mask1 = useRef(null), line1 = useRef(null);
  const grad1Tail = useRef(null), grad1Head = useRef(null);
  const mask2 = useRef(null), line2 = useRef(null);
  const grad2Tail = useRef(null), grad2Head = useRef(null);
  /* Flight path extends past the right-top of the viewBox (95,5 → 130,-20)
     so the plane visibly exits the quote panel before respawning. */
  const D = "M 5 95 C 25 30, 75 20, 130 -20";
  return (
    <div className="vy-cd-quote">
      <svg ref={svgRef} className="vy-cd-deco" viewBox="0 0 100 100"
           preserveAspectRatio="none" aria-hidden="true">
        <defs>
          {/* Path used for sampling positions via getPointAtLength. */}
          <path ref={pathRef} id="vy-flight-path" d={D} fill="none" />
          {/* One mask per plane — each plane reveals only what it has
              flown over. */}
          <mask id="vy-trail-mask-1" maskUnits="userSpaceOnUse"
                x="0" y="0" width="100" height="100">
            <path ref={mask1} d={D} fill="none" stroke="white"
              strokeWidth="20" pathLength="100"
              strokeDasharray="100 100" strokeDashoffset="100"
              strokeLinecap="butt" />
          </mask>
          <mask id="vy-trail-mask-2" maskUnits="userSpaceOnUse"
                x="0" y="0" width="100" height="100">
            <path ref={mask2} d={D} fill="none" stroke="white"
              strokeWidth="20" pathLength="100"
              strokeDasharray="100 100" strokeDashoffset="100"
              strokeLinecap="butt" />
          </mask>
          {/* Comet-tail gradients — `gradientUnits="userSpaceOnUse"` aligns
              with the path's bounding box (5,95)→(95,5). Stops 0/3 sit at
              the path corners and stay transparent; stops 1/2 are moved by
              JS to follow the plane, fading in toward the plane's head. */}
          <linearGradient id="vy-trail-fade-1"
            gradientUnits="userSpaceOnUse"
            x1="5" y1="95" x2="130" y2="-20">
            <stop offset="0" stopColor="white" stopOpacity="0" />
            <stop ref={grad1Tail} offset="0" stopColor="white" stopOpacity="0" />
            <stop ref={grad1Head} offset="1" stopColor="white" stopOpacity="1" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="vy-trail-fade-2"
            gradientUnits="userSpaceOnUse"
            x1="5" y1="95" x2="130" y2="-20">
            <stop offset="0" stopColor="white" stopOpacity="0" />
            <stop ref={grad2Tail} offset="0" stopColor="white" stopOpacity="0" />
            <stop ref={grad2Head} offset="1" stopColor="white" stopOpacity="1" />
            <stop offset="1" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path ref={line1} className="vy-cd-trail-line"
          d={D} fill="none"
          stroke="url(#vy-trail-fade-1)"
          strokeWidth="1" pathLength="100"
          strokeDasharray="2 3"
          mask="url(#vy-trail-mask-1)"
          vectorEffect="non-scaling-stroke" />
        <path ref={line2} className="vy-cd-trail-line"
          d={D} fill="none"
          stroke="url(#vy-trail-fade-2)"
          strokeWidth="1" pathLength="100"
          strokeDasharray="2 3"
          mask="url(#vy-trail-mask-2)"
          vectorEffect="non-scaling-stroke" />
      </svg>
      <FlyingPlane svgRef={svgRef} pathRef={pathRef}
        maskRef={mask1} lineRef={line1}
        gradTailRef={grad1Tail} gradHeadRef={grad1Head}
        planeSrc="/templates/voyage/quote-bg-1.svg" />
      <FlyingPlane svgRef={svgRef} pathRef={pathRef}
        maskRef={mask2} lineRef={line2}
        gradTailRef={grad2Tail} gradHeadRef={grad2Head}
        planeSrc="/templates/voyage/quote-bg-1.svg"
        phaseOffset={0.28} />
      <div className="vy-cd-quote-text">
        {footerQuote.split('\n').map((line, i) => <p key={i}>{line}</p>)}
      </div>
    </div>
  );
}

/* ── Mobile nav ──────────────────────────────────────────────── */
function VoyageNav({ blocks, menu }) {
  const links = [
    blocks.includes('schedule') && { href: '#vy-schedule',  label: menu?.schedule || 'Программа' },
    blocks.includes('map')      && { href: '#vy-location',  label: menu?.location || 'Локация'   },
    blocks.includes('palette')  && { href: '#vy-dresscode', label: menu?.dresscode || 'Дресс-код' },
    blocks.includes('rsvp')     && { href: '#vy-rsvp',      label: menu?.rsvp || 'Анкета'    },
  ].filter(Boolean);
  if (!links.length) return null;
  return (
    <nav className="vy-nav">
      {links.map(l => <a key={l.href} href={l.href}>{l.label}</a>)}
    </nav>
  );
}

/* ── Class maps for shared components ───────────────────────── */
const VY_SCHED_CLASSES = {
  root: 'vy-sched',
  row: 'vy-sched-row',
  time: 'vy-sched-time',
  info: 'vy-sched-info',
  name: 'vy-sched-name',
  desc: 'vy-sched-desc',
};
const VY_CD_CLASSES = {
  root: 'vy-cd',
  cell: 'vy-cd-cell',
  num: 'vy-cd-num',
  label: 'vy-cd-lbl',
};
const VY_DRESS_CLASSES = {
  wrap: 'vy-swatches',
  swatchWrap: 'vy-swatch-wrap',
  swatch: 'vy-swatch',
  swatchLabel: 'vy-swatch-lbl',
  photos: 'vy-dc-photos',
  photo: 'vy-dc-photo',
};
const VY_RSVP_CLASSES = {
  form:    'vy-form',
  tabs:    'vy-rsvp-tabs',
  tab:     'vy-rsvp-tab',
  tabSel:  'vy-rsvp-tab--sel',
  field:   'vy-form-field',
  label:   'vy-form-lbl',
  input:   'vy-form-input',
  submit:  'vy-btn vy-btn--primary vy-btn--full',
  success: 'vy-form-ok',
  error:   'vy-form-err',
  chip:    'vy-chip',
  chipSel: 'vy-chip--sel',
  chips:   'vy-form-chips',
};
const VY_WL_CLASSES = {
  list: 'vy-wish',
  item: 'vy-wish-item',
  itemBorder: '',
  row: 'vy-wish-row',
  name: 'vy-wish-name',
  price: 'vy-wish-price',
  btn: 'vy-btn vy-btn--outline vy-btn--full',
  btnReserved: 'vy-btn--reserved',
};
const VY_WL_MODAL_CLASSES = {
  bg:         'vy-wish-modal-bg',
  modal:      'vy-wish-modal',
  title:      'vy-wish-modal-title',
  subtitle:   'vy-wish-modal-sub',
  input:      'vy-form-input',
  error:      'vy-form-err',
  btns:       'vy-wish-modal-btns',
  btnCancel:  'vy-btn vy-btn--outline',
  btnConfirm: 'vy-btn vy-btn--primary',
};

/* ══ MAIN ══════════════════════════════════════════════════════ */
export default function Voyage({ project, design, isPreview }) {
  const pageRef = useRef(null);
  /* Футер-фото появляется/исчезает независимо от блоков — включаем его в
     зависимость, иначе useReveal не пересканирует DOM и только что
     добавленный футер остаётся скрытым (opacity:0) в превью. */
  const blockDep = `${project?.blocks?.length}|${project?.footer_photo?.url ? 1 : 0}`;
  useReveal(pageRef, blockDep, { revealSelector: '.vy-reveal', inClass: 'vy-in' });
  useVoyageFonts();

  const [inPreview, setInPreview] = useState(!!isPreview);
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    setInPreview(!!isPreview || !!root.closest('.lp-phone-screen,.lp-template-inner,[class*="lp-"]'));
  }, [isPreview]);

  const p = project || {};
  const d = design  || {};
  const bc = p.block_content || {};

  /* Colours — palette overrides (project/design). */
  const accent = d.palette?.primary   || p.palette?.primary   || '#6579FF';
  const fg     = d.palette?.textDark  || p.palette?.textDark  || '#232323';

  /* Blocks */
  const blocks = p.blocks || ['menu','countdown','schedule','map','palette','rsvp','survey','message','wishlist','calendar'];
  const HAS_MENU     = blocks.includes('menu');
  const HAS_CALENDAR = blocks.includes('calendar');
  const HAS_RSVP     = blocks.includes('rsvp');

  /* Text */
  const bride    = p.bride  || 'Александра';
  const groom    = p.groom  || 'Богдан';
  const tagline  = bc.header?.subtitle || p.wedding_slogan || 'Не прошло и 5-ти лет';
  const invTitle = bc.message?.title  || 'Дорогие наши!';
  const invText  = p.invitation_text || bc.message?.text || '';
  const footerQuote = bc.footer?.quote || 'Одна любовь,\nдругой такой\nи не было';

  /* Photos */
  const photos = p.cover_photos || (p.cover_photo ? [{ url: p.cover_photo }] : []);
  const dcPhotos = p.dresscode_photos || [];
  /* Футер-фото независимо от главного: если не задано — футер без фото. */
  const footerPhotoObj = p.footer_photo?.url ? p.footer_photo : null;
  const footerPhoto    = src(footerPhotoObj?.url) || null;

  /* Date / schedule / venues / wishlist / dresscode */
  const dateStr = fmtDateDots(p.wedding_date);
  const schedule = p.schedule?.length ? p.schedule : [];
  const venues   = (p.venues || []).filter(v => (v.name || "").trim() || (v.address || "").trim());
  const wishlist = p.wishlist || [];
  const swatches = p.dresscode_palette || [];

  /* Titles */
  const titleSchedule = bc.schedule?.title  || 'Программа';
  const titleLocation = bc.map?.title       || 'Локация';
  const titleDress    = bc.palette?.title   || 'Дресс-код';
  const titleRsvp     = bc.rsvp?.title      || 'Подтвердите присутствие';
  const titleWish     = bc.wishlist?.title  || 'Что дарить';
  const titleCD       = bc.countdown?.title || 'До нашей свадьбы';
  const calBtn        = bc.calendar?.title  || 'Добавить в календарь';

  return (
    <div className={`vy-page${inPreview ? ' vy-page--preview' : ''}`} ref={pageRef}
      style={{ '--vy-accent': accent, '--vy-accent-rgb': accentToRgb(accent), '--vy-fg': fg }}>
      <PaidWatermark isPaid={project?.is_paid} isPreview={inPreview} />

      {HAS_MENU && <VoyageNav blocks={blocks} menu={bc.menu} />}

      {/* ── HERO — 3-D tilt follows the cursor (desktop / mouse devices).
           Mobile gets the same lifted shadow but no tilt (no pointer).
           See the `onMouseMove` handler: x/y are mapped to rotateY/X plus
           a small lift via translateZ so the ticket "floats". */}
      <section className="vy-hero vy-reveal"
        onMouseMove={e => {
          const el = e.currentTarget;
          const rect = el.getBoundingClientRect();
          const x = (e.clientX - rect.left) / rect.width - 0.5;
          const y = (e.clientY - rect.top) / rect.height - 0.5;
          el.style.setProperty('--vy-tx', `${x * 10}deg`);
          el.style.setProperty('--vy-ty', `${-y * 7}deg`);
          el.style.setProperty('--vy-tz', '14px');
        }}
        onMouseLeave={e => {
          const el = e.currentTarget;
          el.style.removeProperty('--vy-tx');
          el.style.removeProperty('--vy-ty');
          el.style.removeProperty('--vy-tz');
        }}>
        <div className="vy-hero-main">
          <div className="vy-hero-text-col">
            {tagline && <span className="vy-hero-pill">{tagline}</span>}
            <div className="vy-hero-names">
              <p className="vy-hero-name">{bride}</p>
              <p className="vy-hero-amp">{bc.header?.amp || '&'}</p>
              <p className="vy-hero-name">{groom}</p>
            </div>
          </div>
          <div className="vy-hero-map">
            <img src="/templates/voyage/worldmap.svg" alt="" aria-hidden="true" />
          </div>
        </div>
        <div className="vy-hero-side">
          <div className="vy-hero-date-rotor">
            <p className="vy-hero-date">{dateStr}</p>
          </div>
        </div>
      </section>

      {/* ── INVITATION + DATE CARD (2-col on desktop) ───────── */}
      <section className="vy-sect vy-invite-row vy-reveal">
        <div className="vy-invite-cell">
          <h2 className="vy-h">{invTitle}</h2>
          <div className="vy-invite-body">
            {invText
              ? invText.split('\n').map((line, i) =>
                  line.trim() ? <p key={i}>{line}</p> : <br key={i} />)
              : (
                <>
                  <p>С огромным волнением и радостью хотим</p>
                  <p>пригласить вас на ужин в честь нашего</p>
                  <p>бракосочетания!</p>
                  <br />
                  <p>В этот особенный день мы будем счастливы видеть каждого из вас, чтобы разделить волшебство момента и создать воспоминания, которые останутся в наших сердцах навсегда.</p>
                </>
              )
            }
          </div>
        </div>
        <div className="vy-date-cell vy-ticket">
          <p className="vy-date-num">{dateStr}</p>
          {HAS_CALENDAR && (
            <CalendarButton
              bride={bride}
              groom={groom}
              date={p.wedding_date}
              time={p.wedding_time}
              venues={p.venues}
              address={p.wedding_address}
              className="vy-btn vy-btn--primary vy-btn--full">
              {calBtn}
            </CalendarButton>
          )}
        </div>
      </section>

      {/* ── Dynamic blocks in Figma order ────────────────────── */}
      {['map','schedule','palette','rsvp','wishlist','countdown']
        .filter(b => blocks.includes(b))
        .map(blockId => {
          switch (blockId) {
            case 'map':
              return (
                <section key="map" className="vy-sect vy-map-sect vy-reveal" id="vy-location">
                  <h2 className="vy-h">{titleLocation}</h2>
                  <BlockSubtitle text={bc.map?.subtitle} className="vy-section-sub" />
                  {venues.map((v, i) => (
                    <div key={i} className="vy-addr">
                      <svg className="vy-addr-icon" viewBox="0 0 42 42" fill="currentColor" aria-hidden="true">
                        <path d="M19.9537 15.3666L36.1056 11.7194L38.5804 14.1943L25.5548 20.9676L32.187 27.5998L37.9617 25.9499L39.8179 27.8061L33.012 32.1371L28.6809 38.943L26.8248 37.0868L28.4747 31.3121L21.8425 24.6799L15.0691 37.7056L12.5943 35.2307L16.2414 19.0789L9.50066 12.3381C8.47553 11.313 8.47557 9.6509 9.50066 8.6258C10.5258 7.60071 12.1878 7.60067 13.213 8.6258L19.9537 15.3666Z" />
                      </svg>
                      <span className="vy-addr-text">
                        {v.name && v.address ? `${v.name}, ${v.address}` : (v.name || v.address)}
                      </span>
                    </div>
                  ))}
                  {!venues.length && p.wedding_address && (
                    <div className="vy-addr">
                      <svg className="vy-addr-icon" viewBox="0 0 42 42" fill="currentColor" aria-hidden="true">
                        <path d="M19.9537 15.3666L36.1056 11.7194L38.5804 14.1943L25.5548 20.9676L32.187 27.5998L37.9617 25.9499L39.8179 27.8061L33.012 32.1371L28.6809 38.943L26.8248 37.0868L28.4747 31.3121L21.8425 24.6799L15.0691 37.7056L12.5943 35.2307L16.2414 19.0789L9.50066 12.3381C8.47553 11.313 8.47557 9.6509 9.50066 8.6258C10.5258 7.60071 12.1878 7.60067 13.213 8.6258L19.9537 15.3666Z" />
                      </svg>
                      <span className="vy-addr-text">{p.wedding_address}</span>
                    </div>
                  )}
                  {/* Карта — только при наличии адреса (имя без адреса → без карты). */}
                  {(p.map_url || (p.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
                  <div className="vy-map-wrap">
                    {(() => {
                      if (p.map_url) {
                        return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={p.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                      }
                      const withCoords = venues.filter(v => Number.isFinite(+v.lat) && Number.isFinite(+v.lon));
                      if (withCoords.length > 0) {
                        return <YandexMap points={withCoords} singleZoom={16} region={p.region} provider={bc.map?.provider} preset="islands#violetIcon" />;
                      }
                      const addr = venues.length > 0
                        ? venues.map(v => v.address).filter(Boolean).join(' — ')
                        : (p.wedding_address || '');
                      if (!addr) {
                        return <img src="/templates/voyage/map.jpg" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
                      }
                      const s = `https://yandex.ru/map-widget/v1/?mode=search&text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                      return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={s} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                    })()}
                  </div>
                  )}
                </section>
              );

            case 'schedule': {
              /* Пусто (удалили все пункты) → только заголовок и подзаголовок. */
              const items = schedule;
              return (
                <section key="schedule" className="vy-sect vy-sched-sect vy-reveal" id="vy-schedule">
                  <h2 className="vy-h">{titleSchedule}</h2>
                  <BlockSubtitle text={bc.schedule?.subtitle} className="vy-section-sub" />
                  <Schedule items={items} classes={VY_SCHED_CLASSES} />
                </section>
              );
            }

            case 'palette':
              return (
                <section key="palette" className="vy-sect vy-dress-sect vy-reveal" id="vy-dresscode">
                  <div className="vy-dress-head vy-divider">
                    <h2 className="vy-h vy-h--accent">{titleDress}</h2>
                    <BlockSubtitle text={bc.palette?.subtitle} className="vy-dress-desc" tag="div" />
                    <DresscodePalette
                      swatches={swatches}
                      cssFilter={flt}
                      classes={{ wrap: 'vy-swatches', swatchWrap: 'vy-swatch-wrap', swatch: 'vy-swatch', swatchLabel: 'vy-swatch-lbl' }}
                    />
                  </div>
                  {dcPhotos.length > 0 && (
                    <div className="vy-dc-photos">
                      {dcPhotos.map((ph, i) => (
                        <div key={i} className="vy-dc-photo">
                          <img src={src(ph.url)} alt="" style={{ filter: flt(ph), objectPosition: objPos(ph) }} />
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );

            case 'rsvp':
              return (
                <section key="rsvp" className="vy-sect vy-rsvp-sect vy-reveal" id="vy-rsvp">
                  <h2 className="vy-h">{titleRsvp}</h2>
                  <BlockSubtitle text={bc.rsvp?.subtitle} className="vy-section-sub" />
                  <RSVPForm project={p} blocks={blocks} classes={VY_RSVP_CLASSES} />
                </section>
              );

            case 'wishlist':
              return (
                <section key="wishlist" className="vy-sect vy-wish-sect vy-ticket vy-reveal">
                  <div className="vy-wish-head">
                    <h2 className="vy-h vy-h--accent">{titleWish}</h2>
                    <BlockSubtitle text={bc.wishlist?.subtitle} className="vy-wish-desc" />
                  </div>
                  <Wishlist
                    items={wishlist} currency={bc.wishlist?.currency} reserveText={bc.wishlist?.reserveText}
                    slug={p.slug}
                    classes={VY_WL_CLASSES}
                    modalClasses={VY_WL_MODAL_CLASSES}
                  />
                </section>
              );

            case 'countdown':
              return (
                <section key="countdown" className="vy-sect vy-cd-sect vy-reveal">
                  <div className="vy-cd-block">
                    <div className="vy-cd-top vy-divider">
                      <h2 className="vy-h vy-h--white">{titleCD}</h2>
                      <BlockSubtitle text={bc.countdown?.subtitle} className="vy-section-sub" style={{ color: 'var(--vy-white)' }} />
                      <Countdown date={p.wedding_date} time={p.wedding_time} bc={bc} classes={VY_CD_CLASSES} />
                    </div>
                    <FlightDeco footerQuote={footerQuote} />
                  </div>
                </section>
              );

            default:
              return null;
          }
        })}

      {/* ── FOOTER PHOTO ────────────────────────────────────── */}
      {/* Нет своего фото — секцию не показываем (без дефолтной картинки). */}
      {footerPhoto && (
        <footer className="vy-sect vy-footer-sect vy-reveal">
          <div className="vy-footer-photo">
            <img src={footerPhoto} alt="" style={{ filter: flt(footerPhotoObj), objectPosition: objPos(footerPhotoObj) }} />
          </div>
        </footer>
      )}

      {/* ── Fixed CTA ──────────────────────────────────────────── */}
      {HAS_RSVP && (
        <FixedCTA
          href="#vy-rsvp"
          targetId="vy-rsvp"
          scrollRoot={pageRef}
          className="vy-fixed-cta">
          {bc.rsvp?.ctaText || 'Подтвердить присутствие'}
        </FixedCTA>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={inPreview} />
      )}
    </div>
  );
}
